import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
} from "react";
import { api } from "../api";
import { useAuth } from "./AuthContext";
import { useSettings } from "./SettingsContext";
import { useVisibility } from "./VisibilityContext";
import { devLog, logError } from "../utils/logger";

/**
 * NotificationContext
 *
 * Manages shared notification state across the application.
 * Primarily used for the unread notification count displayed in the Sidebar badge.
 *
 * Features:
 * - Automatic polling every 60 seconds to keep count fresh
 * - Manual refetch capability for immediate updates (e.g., after marking as read)
 * - Debouncing to prevent race conditions between polling and manual refetches
 *
 * Usage:
 * - Wrap your app with <NotificationProvider>
 * - Use useNotificationContext() hook in components that need notification state
 */

const NotificationContext = createContext(null);

export function NotificationProvider({ children }) {
  const { user } = useAuth();
  const { settings } = useSettings();
  const { isVisible } = useVisibility();
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Debouncing flag to prevent multiple simultaneous fetches
  const isFetching = useRef(false);

  // Circuit breaker: stop polling after a 401 auth failure
  const authFailed = useRef(false);

  // Track whether this is the first mount (to avoid double fetch with mount effect)
  const hasPolledOnce = useRef(false);

  /**
   * Fetches the current unread notification count from the API
   * Debounced to prevent race conditions when polling and manual calls overlap
   * Filters count based on user's notification preferences
   */
  const fetchUnreadCount = useCallback(async () => {
    if (!user?.id || authFailed.current) {
      setLoading(false);
      return;
    }

    // Debounce: if already fetching, skip this call
    if (isFetching.current) {
      devLog("NotificationContext: Already fetching, skipping duplicate call");
      return;
    }

    isFetching.current = true;

    try {
      setError(null);
      const response = await api.getNotificationStats();
      const stats = response.data;

      // Filter count based on user's notification preferences
      const notificationPrefs = settings?.notifications || {
        likes: true,
        comments: true,
        system: true,
      };

      let filteredCount = 0;
      if (notificationPrefs.likes) filteredCount += stats.likes || 0;
      if (notificationPrefs.comments) filteredCount += stats.comments || 0;
      if (notificationPrefs.system) filteredCount += stats.system || 0;

      setUnreadCount(filteredCount);
    } catch (err) {
      // Stop polling on auth failure to prevent 401 cascade
      if (err.statusCode === 401) {
        authFailed.current = true;
      }
      setError(err.message);
      logError("NotificationContext: Failed to fetch unread count:", err);
    } finally {
      setLoading(false);
      isFetching.current = false;
    }
  }, [user, settings]);

  // Reset circuit breaker when user changes (e.g., re-login)
  useEffect(() => {
    authFailed.current = false;
  }, [user]);

  // Fetch on mount and when user changes
  useEffect(() => {
    if (!user?.id) return;
    fetchUnreadCount();
  }, [fetchUnreadCount, user]);

  // Set up polling interval to keep count fresh (every 60 seconds)
  // Pauses when the tab is hidden and resumes with an immediate fetch when visible
  useEffect(() => {
    if (!user?.id || !isVisible) return;

    // Immediate fetch when tab becomes visible again (skip on first mount — handled above)
    if (hasPolledOnce.current) {
      fetchUnreadCount();
    }
    hasPolledOnce.current = true;

    const interval = setInterval(() => {
      devLog("NotificationContext: Polling for unread count update");
      fetchUnreadCount();
    }, 60000); // 60 seconds

    return () => clearInterval(interval);
  }, [fetchUnreadCount, user, isVisible]);

  const value = {
    unreadCount,
    loading,
    error,
    refetchUnreadCount: fetchUnreadCount, // Expose for manual triggers
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
}

/**
 * Hook to access notification context
 *
 * @returns {Object} { unreadCount, loading, error, refetchUnreadCount }
 *
 * @example
 * const { unreadCount, refetchUnreadCount } = useNotificationContext();
 *
 * // Display badge
 * {unreadCount > 0 && <Badge count={unreadCount} />}
 *
 * // Manually refresh after marking as read
 * await markAsRead(notificationId);
 * await refetchUnreadCount();
 */
export function useNotificationContext() {
  const context = useContext(NotificationContext);

  if (!context) {
    throw new Error(
      "useNotificationContext must be used within a NotificationProvider. " +
        "Make sure to wrap your app with <NotificationProvider>.",
    );
  }

  return context;
}
